import React, { useMemo } from 'react';
import { FormDefaults } from '../types';
import { APITypes } from '../../../../../../primary-record-types';
import {
    FormValue,
    clientRecordToFormValue,
} from '../../../../../shared/OrganizationClientInvitations/OrganizationClientInvitationFields';
import FocusedInviteForm from './FocusedInviteForm';
import { InviteClientRecordArgs } from '../../../../../../hooks/api/useOrganizationClientRecords';

export type InviteValues = {
    request: FormValue;
    organizationClientRecordId: string;
};

export type InviteFormsOwnProps = {
    formDefaults: FormDefaults;
    invitationDefinitions: APITypes.organizationService.ListDefinitionsResponse['invitationDefinitions'];
    clientRecords: APITypes.organizationService.GetOrganizationClientRecordResponse[];
    onInvite: (values: InviteClientRecordArgs[]) => void;
    onUndoFormDefaults: () => void;
};

export type InviteFormsProps = InviteFormsOwnProps;

const InviteForms: React.FC<InviteFormsProps> = (props) => {
    const asFormValues = useMemo(
        () =>
            props.clientRecords.map<InviteValues>((_) => ({
                request: {
                    ...clientRecordToFormValue(_, props.invitationDefinitions),
                    ...props.formDefaults,
                },
                organizationClientRecordId: _.organizationClientRecordId,
            })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );
    const [completedFormValues, setCompletedFormValues] = React.useState<InviteValues[]>([]);
    const [pendingFormValues, setPendingFormValues] = React.useState<InviteValues[]>(asFormValues);

    const handleOnPrevious = () => {
        if (completedFormValues.length === 0) {
            props.onUndoFormDefaults();
        } else {
            const copiedCompletedFormValues = [...completedFormValues];
            setPendingFormValues([copiedCompletedFormValues.pop()!, ...pendingFormValues]);
            setCompletedFormValues(copiedCompletedFormValues);
        }
    };

    const focusedFormValue = pendingFormValues[0];

    const handleOnNext = (values: FormValue) => {
        const inviteValues = {
            request: {
                ...values,
                ...props.formDefaults,
            },
            organizationClientRecordId: focusedFormValue.organizationClientRecordId,
        };
        const newCompletedFormValues = [...completedFormValues, inviteValues];
        if (pendingFormValues.length <= 1) {
            props.onInvite(newCompletedFormValues);
        } else {
            setCompletedFormValues(newCompletedFormValues);
            setPendingFormValues([...pendingFormValues].slice(1));
        }
    };

    return (
        <FocusedInviteForm
            key={JSON.stringify(focusedFormValue)}
            initialValues={focusedFormValue.request}
            onSubmit={handleOnNext}
            inviteIndex={completedFormValues.length}
            totalInvites={props.clientRecords.length}
            invitationDefinitions={props.invitationDefinitions}
            onPrevious={handleOnPrevious}
        />
    );
};

export default React.memo(InviteForms);
